<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: How much came in and went out in each period of the window?
  Answer:   the income-against-expense chart per period.
  Context:  the period-by-period table and its totals.
  Controls: the window, bucket and tab strip in the frame.
-->
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CashFlowBarChart,
  CashFlowTable,
  canViewReporting,
  chartPeriods,
  ReportFrame,
  useCashFlowQuery,
  useReportScope,
} from '@/features/reporting'

const { t } = useI18n()
const scope = useReportScope(() => canViewReporting(scope.session.can))
const { session, state, displayCurrency } = scope
const cashFlow = useCashFlowQuery(scope.flows())
const periods = computed(() =>
  chartPeriods(cashFlow.data.value?.periods ?? [], state.range.value, displayCurrency.value),
)
const ascending = computed(() => [...periods.value].reverse())
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.overview.subtitle')" :state="state">
    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canViewReporting(session.can)" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noAccess') }}
    </p>
    <template v-else-if="cashFlow.data.value">
      <figure
        class="flex min-w-0 flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)]"
      >
        <figcaption class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('reporting.chart.incomeVsExpenses') }}
        </figcaption>
        <CashFlowBarChart
          :periods="periods"
          :currency="displayCurrency"
          :bucket="state.range.value.bucket"
          mode="flows"
        />
      </figure>
      <CashFlowTable
        :periods="ascending"
        :totals="cashFlow.data.value"
        :caption="t('reporting.tabs.incomeExpenses')"
      />
    </template>
  </ReportFrame>
</template>
